import { FormsModule } from '@angular/forms';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
  input,
  signal,
} from '@angular/core';
import { Auth } from '@angular/fire/auth';
import axios from 'axios';
import { Subject, take, takeUntil } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Album } from '../../../models/album.model';
import { AlbumImage } from '../../../models/image.album.model';
import { AlbumService } from '../../../services/album.service';
import { UtilsService } from '../../../services/utils.service';
import { AutofocusDirective } from './autofocus.directive';
import { ImageEditorCanvasService } from './canvas/image-editor-canvas.service';
import {
  BRUSH_STYLES,
  DEFAULT_BRUSH_STYLE,
  EditorAiCommand,
  EditorLayer,
  EditorLayerType,
  EditorScene,
  LayerSummary,
  ShapeType,
} from './models/editor-layer.model';
import { GroupBrandKit } from './models/group-brand-kit.model';

const MIN_ZOOM = 0.15;
const MAX_ZOOM = 3;
// A broad, cross-platform (Windows/macOS) web-safe set — these render using
// the OS's own installed font without needing any @font-face/webfont load,
// so every choice here is guaranteed to actually look different rather than
// silently falling back to the same default.
const FONT_CHOICES = [
  'Noto Sans',
  'Noto Sans Malayalam',
  'Arial',
  'Arial Black',
  'Helvetica',
  'Verdana',
  'Tahoma',
  'Trebuchet MS',
  'Segoe UI',
  'Calibri',
  'Cambria',
  'Candara',
  'Consolas',
  'Georgia',
  'Garamond',
  'Times New Roman',
  'Palatino Linotype',
  'Bookman Old Style',
  'Century Gothic',
  'Franklin Gothic Medium',
  'Gill Sans',
  'Rockwell',
  'Courier New',
  'Lucida Console',
  'Impact',
  'Comic Sans MS',
  'Brush Script MT',
  'Copperplate',
  'Papyrus',
];
const SHAPE_CHOICES: { type: ShapeType; label: string; icon: string }[] = [
  { type: 'rect', label: 'Rectangle', icon: 'fa fa-square' },
  { type: 'circle', label: 'Circle', icon: 'fa fa-circle' },
  { type: 'triangle', label: 'Triangle', icon: 'fa fa-play fa-rotate-270' },
  { type: 'diamond', label: 'Diamond', icon: 'fa fa-diamond' },
  { type: 'pentagon', label: 'Pentagon', icon: 'fa fa-draw-polygon' },
  { type: 'hexagon', label: 'Hexagon', icon: 'fa fa-hexagon-nodes' },
  { type: 'star', label: 'Star', icon: 'fa fa-star' },
  { type: 'heart', label: 'Heart', icon: 'fa fa-heart' },
  { type: 'arrow', label: 'Arrow', icon: 'fa fa-arrows-left-right' },
  { type: 'speech-bubble', label: 'Speech bubble', icon: 'fa fa-comment' },
];
const GRID_SIZE_CHOICES = [25, 50, 100];

/**
 * Shared, pluggable image editor — usable from any feature page (notice
 * images, event banners, and future use cases) without that page knowing
 * anything about canvas internals. See IMAGE_EDITOR_REQUIREMENTS.md.
 *
 * Photoshop/Affinity-style chrome: a zoomable artboard in the center (a real
 * Fabric.js canvas — zoom is a CSS transform: scale() on its wrapper, which
 * Fabric correctly remaps pointer coordinates through), a left tool rail for
 * adding layers (a single Photo tool covering background/photo/logo use
 * cases, Text, Shapes, QR), and a right attributes panel bound to whatever's
 * currently selected. See ImageEditorCanvasService for the Fabric.js wrapper.
 */
@Component({
  selector: 'app-image-editor',
  standalone: true,
  imports: [FormsModule, AutofocusDirective],
  providers: [ImageEditorCanvasService],
  templateUrl: './image-editor.component.html',
  styleUrl: './image-editor.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageEditorComponent implements OnInit, AfterViewInit, OnDestroy {
  private canvasService = inject(ImageEditorCanvasService);
  private cdr = inject(ChangeDetectorRef);
  private auth: Auth = inject(Auth);
  private albumService = inject(AlbumService);
  private utilsService = inject(UtilsService);

  @ViewChild('fabricCanvas') canvasElRef?: ElementRef<HTMLCanvasElement>;
  @ViewChild('viewport') viewportRef?: ElementRef<HTMLDivElement>;
  @ViewChild('photoFileInput') photoFileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('replaceFileInput') replaceFileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('importFileInput') importFileInput?: ElementRef<HTMLInputElement>;

  /** Current group context — used for album access, brand kit, and storage paths. */
  groupId = input.required<string>();

  /** Output image aspect ratio. */
  aspectRatio = input<'3:4' | '1:1' | '9:16'>('3:4');

  /**
   * Free-text label identifying the calling feature (e.g. 'notice',
   * 'event-banner'). Used only for storage path segmentation and analytics —
   * the editor itself stays domain-ignorant of Notice/Event/etc.
   */
  contextLabel = input.required<string>();

  /** Restricts which layer tools are offered, for simpler use cases. */
  allowedLayerTypes = input<EditorLayerType[]>([
    'background-image',
    'photo',
    'logo',
    'text',
    'qr',
    'decoration',
  ]);

  /** Reopen/edit a previously saved scene, if the host page persisted one. */
  initialScene = input<EditorScene | undefined>();

  /** Prefills logo/colors/font if the group has a saved brand kit. */
  brandKit = input<GroupBrandKit | undefined>();

  /** Optional starting prompt, e.g. passed in from an AI chat flow. */
  aiSeedPrompt = input<string | undefined>();

  /**
   * Fallback initial background when the host page has an already-generated
   * image URL (e.g. from an AI chat flow) but no full EditorScene to reopen.
   * Seeded onto the canvas as a single background-image layer.
   */
  previewImageUrl = input<string | undefined>();

  /**
   * Pre-signed-URL endpoint the host feature already uses for its own S3
   * uploads (e.g. environment.AWS_SCHOOL_NOTICE_IMAGE_SIGNEDURL) — the saved
   * PNG is uploaded straight to S3 via this same flow, never to Firebase
   * Storage. Required for Save to work.
   */
  preSignedUrlEndpoint = input<string | undefined>();

  /** x-api-key sent with the pre-signed-URL request above. */
  s3ApiKey = input<string | undefined>();

  /** CloudFront domain the uploaded S3 object is served from — the returned bucket URL is rehosted behind this before it's emitted. */
  cloudFrontDomain = input<string | undefined>();

  /** Extra query params merged into the pre-signed-URL request (e.g. `{ noticeId }`) — mirrors app-s3-image-upload's `metadata` input, and per that same backend convention, a `name` here overrides the generated filename. */
  uploadMetadata = input<Record<string, any> | undefined>();

  /**
   * Emits once the user applies: the flattened image is already uploaded to
   * S3 by this point. `scene` is included so a host page can optionally
   * persist it for later re-editing — never required just to display the
   * image.
   */
  @Output() saved = new EventEmitter<{
    imageUrl: string;
    thumbnailUrl?: string;
    scene: EditorScene;
  }>();

  /** No visible button triggers this anymore (Apply is the only action shown), but host pages can still react to it — e.g. an Escape keypress. */
  @Output() cancelled = new EventEmitter<void>();

  /** For host pages that want an unsaved-changes guard. */
  @Output() dirty = new EventEmitter<boolean>();

  private exit$ = new Subject<void>();
  /** The last known-saved scene — seeded from initialScene()/previewImageUrl() at mount, refreshed after every successful Save. The Cancel button reverts to this. */
  private lastSavedScene: EditorScene | null = null;

  zoom = signal(1);
  activeLayer = signal<EditorLayer | null>(null);
  /** How many objects are currently selected — drives the topbar's multi-select alignment menu (shown only for 2+). */
  selectionCount = signal(0);
  showAlignMenu = signal(false);
  /** All layers, topmost (frontmost) first — backs the always-visible Layers panel. Refreshed on every changed$ tick, since every mutation path (add/remove/reorder/rebuild) already funnels through it. */
  layers = signal<EditorLayer[]>([]);
  isSaving = signal(false);
  isBusy = signal(false); // true while an async add/replace (image load, QR render) is in flight
  showQrPrompt = signal(false);
  qrUrlInput = '';
  showShapeMenu = signal(false);
  /** "File" rail tool popover — Import / Export layered-file options. */
  showFileMenu = signal(false);
  isDirty = signal(false);

  /** Set on any failed async op (photo/album/QR load, Save) so the user sees *something* instead of a silent no-op — dismissible via the banner's close button. */
  editorError = signal<string | null>(null);

  /** Layers panel inline rename — which layer id (if any) is currently being renamed, and its in-progress value. */
  editingLayerId = signal<string | null>(null);
  editingLayerName = '';

  // Photo tool — a single left-rail button opens this small popover with
  // "From device" / "From albums" (background/photo/logo all collapse into
  // one generic "photo" concept from the UI's point of view).
  showPhotoPicker = signal(false);
  photoPickerView = signal<'menu' | 'albums' | 'album-images'>('menu');
  albums = signal<Album[]>([]);
  albumImages = signal<AlbumImage[]>([]);
  selectedAlbum = signal<Album | null>(null);
  albumsLoading = signal(false);
  albumImagesLoading = signal(false);

  // Snap grid — a plain CSS overlay drawn by the template (never a Fabric
  // object, so it's never part of the exported PNG); enabling it also turns
  // on hard snap-to-grid while dragging, via ImageEditorCanvasService.setSnap().
  // On by default for precise placement out of the box.
  gridEnabled = signal(true);
  gridSize = signal(50);

  /** Artboard resize drag state — see startArtboardResize()/onArtboardResizeMove(). Not a signal: mutated every pointermove, so a plain field avoids needless change-detection churn mid-drag. */
  private artboardResizeStart: { x: number; y: number; width: number; height: number; dir: 'e' | 's' | 'se' } | null = null;

  fontChoices = FONT_CHOICES;
  shapeChoices = SHAPE_CHOICES;
  gridSizeChoices = GRID_SIZE_CHOICES;

  get zoomPercent(): number {
    return Math.round(this.zoom() * 100);
  }

  /** Delegates to the canvas service, which tracks the LIVE size (seeded from the aspectRatio preset, but overridable via resizeArtboard()) rather than a fixed lookup — so this always reflects any manual resize. */
  get artboardSize() {
    return this.canvasService.artboardSize;
  }

  ngOnInit(): void {
    this.canvasService.selection$.pipe(takeUntil(this.exit$)).subscribe((layer) => {
      this.activeLayer.set(layer);
      this.cdr.markForCheck();
    });
    this.canvasService.selectionCount$.pipe(takeUntil(this.exit$)).subscribe((count) => {
      this.selectionCount.set(count);
      if (count <= 1) this.showAlignMenu.set(false);
      this.cdr.markForCheck();
    });
    this.canvasService.changed$.pipe(takeUntil(this.exit$)).subscribe(() => {
      this.isDirty.set(true);
      this.dirty.emit(true);
      this.refreshLayers();
      this.cdr.markForCheck();
    });
  }

  /** Re-reads the full layer list from the canvas, sorted frontmost-first, for the Layers panel. */
  private refreshLayers(): void {
    this.layers.set(
      this.canvasService.getScene().layers.slice().sort((a, b) => b.zIndex - a.zIndex),
    );
  }

  ngAfterViewInit(): void {
    const el = this.canvasElRef?.nativeElement;
    if (!el) return;

    let scene = this.initialScene();
    if (!scene && this.previewImageUrl()) {
      scene = {
        version: 1,
        aspectRatio: this.aspectRatio(),
        layers: [
          {
            id: 'background',
            type: 'background-image',
            x: 0,
            y: 0,
            width: 0,
            height: 0,
            rotation: 0,
            zIndex: 0,
            props: { sourceUrl: this.previewImageUrl() },
          },
        ],
      };
    }

    this.canvasService.initCanvas(el, this.aspectRatio(), scene);
    this.canvasService.setSnap(this.gridEnabled(), this.gridSize());
    this.lastSavedScene = scene ? JSON.parse(JSON.stringify(scene)) : null;
    this.isDirty.set(false);

    // Give layout a tick to settle, then fit the artboard to the viewport.
    setTimeout(() => this.zoomToFit(), 0);
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
    this.canvasService.destroy();
    window.removeEventListener('pointermove', this.onArtboardResizeMove);
    window.removeEventListener('pointerup', this.onArtboardResizeEnd);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.zoomToFit();
  }

  @HostListener('window:keydown.escape')
  onEscape(): void {
    if (this.showPhotoPicker()) {
      this.showPhotoPicker.set(false);
      return;
    }
    if (this.showShapeMenu()) {
      this.showShapeMenu.set(false);
      return;
    }
    if (this.showFileMenu()) {
      this.showFileMenu.set(false);
      return;
    }
    if (this.showOpacityPopupFor()) {
      this.showOpacityPopupFor.set(null);
      return;
    }
    if (this.showAlignMenu()) {
      this.showAlignMenu.set(false);
      return;
    }
    if (this.showQrPrompt()) {
      this.closeQrPrompt();
      return;
    }
    // Deliberately no whole-editor cancel-on-Escape fallback here — there's
    // no visible close button by design (Apply is the only exit action), so
    // Escape should only dismiss the small transient popovers above.
  }

  /**
   * Copy/paste/group/ungroup/delete for whatever's selected on the canvas.
   * Bound at window level (like onEscape above) rather than only on the
   * canvas element, so shortcuts work regardless of which part of the editor
   * has focus — guarded by isTypingTarget() so they never hijack keystrokes
   * meant for a real text field, INCLUDING Fabric's own IText inline editor,
   * which is backed by a real <textarea> Fabric appends to the DOM while
   * editing (so the same tagName check covers both cases for free).
   */
  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (this.isTypingTarget(event.target)) return;
    const meta = event.ctrlKey || event.metaKey;

    if (!meta && (event.key === 'Delete' || event.key === 'Backspace')) {
      event.preventDefault();
      this.canvasService.deleteSelection();
      return;
    }
    if (meta && event.key.toLowerCase() === 'c') {
      event.preventDefault();
      this.canvasService.copySelection();
      return;
    }
    if (meta && event.key.toLowerCase() === 'v') {
      event.preventDefault();
      this.canvasService.pasteClipboard();
      return;
    }
    if (meta && event.key.toLowerCase() === 'g') {
      event.preventDefault();
      if (event.shiftKey) {
        this.canvasService.ungroupSelection();
      } else {
        this.canvasService.groupSelection();
      }
      return;
    }
    if (
      !meta &&
      (event.key === 'ArrowUp' ||
        event.key === 'ArrowDown' ||
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowRight')
    ) {
      // Nudge whatever's selected — 1px per press, 10px with Shift held, same
      // convention as Photoshop/Figma. Works for a single object or a
      // multi-object selection alike (see nudgeSelection).
      event.preventDefault();
      const step = event.shiftKey ? 10 : 1;
      const dx = event.key === 'ArrowLeft' ? -step : event.key === 'ArrowRight' ? step : 0;
      const dy = event.key === 'ArrowUp' ? -step : event.key === 'ArrowDown' ? step : 0;
      this.canvasService.nudgeSelection(dx, dy);
      return;
    }
  }

  private isTypingTarget(target: EventTarget | null): boolean {
    const el = target as HTMLElement | null;
    if (!el) return false;
    return (
      el.tagName === 'INPUT' ||
      el.tagName === 'TEXTAREA' ||
      el.tagName === 'SELECT' ||
      el.isContentEditable
    );
  }

  // ─────────────────────────────────────────────
  // ZOOM
  // ─────────────────────────────────────────────

  setZoom(value: number): void {
    this.zoom.set(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value)));
  }

  zoomIn(): void {
    this.setZoom(this.zoom() + 0.1);
  }

  zoomOut(): void {
    this.setZoom(this.zoom() - 0.1);
  }

  zoomTo100(): void {
    this.setZoom(1);
  }

  zoomToFit(): void {
    const viewport = this.viewportRef?.nativeElement;
    if (!viewport) return;
    const { width, height } = this.artboardSize;
    const padding = 64;
    const availW = viewport.clientWidth - padding;
    const availH = viewport.clientHeight - padding;
    if (availW <= 0 || availH <= 0) return;
    this.setZoom(Math.min(availW / width, availH / height));
  }

  onWheel(event: WheelEvent): void {
    if (!event.ctrlKey && !event.metaKey) return; // only zoom on pinch/ctrl+wheel, otherwise let the viewport scroll normally
    event.preventDefault();
    const delta = event.deltaY > 0 ? -0.05 : 0.05;
    this.setZoom(this.zoom() + delta);
  }

  // ─────────────────────────────────────────────
  // SNAP GRID
  // ─────────────────────────────────────────────

  toggleGrid(): void {
    this.gridEnabled.update((v) => !v);
    this.canvasService.setSnap(this.gridEnabled(), this.gridSize());
  }

  setGridSize(size: number): void {
    this.gridSize.set(size);
    if (this.gridEnabled()) this.canvasService.setSnap(true, size);
  }

  // ─────────────────────────────────────────────
  // ARTBOARD RESIZE — numeric width/height (shown when nothing's selected)
  // and drag handles on the artboard's right/bottom/corner edges.
  // ─────────────────────────────────────────────

  setArtboardWidth(width: number): void {
    if (!width || width < 100) return;
    this.canvasService.resizeArtboard(Math.round(width), this.artboardSize.height);
    this.zoomToFit();
    this.cdr.markForCheck();
  }

  setArtboardHeight(height: number): void {
    if (!height || height < 100) return;
    this.canvasService.resizeArtboard(this.artboardSize.width, Math.round(height));
    this.zoomToFit();
    this.cdr.markForCheck();
  }

  /** Starts a drag-resize from one of the handles around the artboard (see the template's .resize-handle elements). Anchored top-left — 'e' grows/shrinks width, 's' height, 'se' both. */
  startArtboardResize(event: PointerEvent, dir: 'e' | 's' | 'se'): void {
    event.preventDefault();
    event.stopPropagation();
    const { width, height } = this.artboardSize;
    this.artboardResizeStart = { x: event.clientX, y: event.clientY, width, height, dir };
    window.addEventListener('pointermove', this.onArtboardResizeMove);
    window.addEventListener('pointerup', this.onArtboardResizeEnd);
  }

  private onArtboardResizeMove = (event: PointerEvent): void => {
    const start = this.artboardResizeStart;
    if (!start) return;
    const zoom = this.zoom() || 1;
    const dx = (event.clientX - start.x) / zoom;
    const dy = (event.clientY - start.y) / zoom;
    let width = start.width;
    let height = start.height;
    if (start.dir === 'e' || start.dir === 'se') width = Math.round(start.width + dx);
    if (start.dir === 's' || start.dir === 'se') height = Math.round(start.height + dy);
    width = Math.max(200, Math.min(4000, width));
    height = Math.max(200, Math.min(4000, height));
    this.canvasService.resizeArtboard(width, height);
    this.cdr.markForCheck();
  };

  private onArtboardResizeEnd = (): void => {
    this.artboardResizeStart = null;
    window.removeEventListener('pointermove', this.onArtboardResizeMove);
    window.removeEventListener('pointerup', this.onArtboardResizeEnd);
    this.zoomToFit();
    this.cdr.markForCheck();
  };

  // ─────────────────────────────────────────────
  // MULTI-SELECT ALIGNMENT — topbar dropdown, shown only for 2+ selected
  // ─────────────────────────────────────────────

  toggleAlignMenu(): void {
    this.showAlignMenu.update((v) => !v);
  }

  alignSelection(mode: 'left' | 'center-h' | 'right' | 'top' | 'middle-v' | 'bottom'): void {
    this.canvasService.alignSelection(mode);
  }

  /** Folds the current multi-selection into one Group layer — same action as the Ctrl/Cmd+G shortcut, exposed as a button in the attributes panel for when 2+ objects are selected. */
  groupSelectedLayers(): void {
    this.canvasService.groupSelection();
  }

  /** Un-does groupSelectedLayers() — restores the group's contents as a live multi-selection. Same action as Ctrl/Cmd+Shift+G. */
  ungroupActiveLayer(): void {
    this.canvasService.ungroupSelection();
  }

  // ─────────────────────────────────────────────
  // LEFT RAIL — ADD LAYER TOOLS
  // ─────────────────────────────────────────────

  isAllowed(type: EditorLayerType): boolean {
    return this.allowedLayerTypes().includes(type);
  }

  addTextLayer(): void {
    this.canvasService.addLayer({
      id: crypto.randomUUID(),
      type: 'text',
      x: this.artboardSize.width / 2,
      y: this.artboardSize.height / 2,
      width: 0,
      height: 0,
      rotation: 0,
      zIndex: 0,
      props: {
        text: 'Your text here',
        fontFamily: this.brandKit()?.preferredFontFamily || 'Noto Sans',
        fontSize: 42,
        color: this.brandKit()?.primaryColor || '#1f2937',
        align: 'center',
        curve: 0,
      },
    });
  }

  toggleShapeMenu(): void {
    this.showPhotoPicker.set(false);
    this.showFileMenu.set(false);
    this.showShapeMenu.update((v) => !v);
  }

  /** "File" rail tool — Import a saved layered image, or Export the current one. */
  toggleFileMenu(): void {
    this.showPhotoPicker.set(false);
    this.showShapeMenu.set(false);
    this.showFileMenu.update((v) => !v);
  }

  addShapeLayer(shapeType: ShapeType): void {
    this.showShapeMenu.set(false);
    this.canvasService.addLayer({
      id: crypto.randomUUID(),
      type: 'decoration',
      x: this.artboardSize.width / 2,
      y: this.artboardSize.height / 2,
      width: 0,
      height: 0,
      rotation: 0,
      zIndex: 0,
      props: {
        shapeType,
        fillMode: 'solid',
        fill: this.brandKit()?.secondaryColor || '#2781ff',
        fillOpacity: 100,
        fillGradientStops: this.defaultGradientStops.map((s) => ({ ...s })),
        strokeStyle: 'none',
        strokeMode: 'solid',
        stroke: '#000000',
        strokeWidth: 4,
        strokeGradientStops: [
          { offset: 0, color: '#111827' },
          { offset: 100, color: '#4b5563' },
        ],
        blurType: 'none',
        blurAmount: 8,
      },
    });
  }

  // ── Photo popover: "From device" / "From albums" ──

  togglePhotoPicker(): void {
    this.showShapeMenu.set(false);
    this.showFileMenu.set(false);
    this.photoPickerView.set('menu');
    this.showPhotoPicker.update((v) => !v);
  }

  closePhotoPicker(): void {
    this.showPhotoPicker.set(false);
  }

  choosePhotoFromDevice(): void {
    this.showPhotoPicker.set(false);
    this.photoFileInput?.nativeElement.click();
  }

  choosePhotoFromAlbums(): void {
    this.photoPickerView.set('albums');
    if (this.albums().length === 0) this.loadAlbums();
  }

  backToPhotoMenu(): void {
    this.photoPickerView.set('menu');
  }

  backToAlbumList(): void {
    this.photoPickerView.set('albums');
  }

  private loadAlbums(): void {
    if (!this.groupId()) return;
    this.albumsLoading.set(true);
    this.albumService
      .getAlbums({ groupId: this.groupId() })
      .pipe(take(1))
      .subscribe({
        next: (albums) => {
          this.albums.set(albums);
          this.albumsLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.albumsLoading.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  selectAlbum(album: Album): void {
    this.selectedAlbum.set(album);
    this.photoPickerView.set('album-images');
    this.albumImagesLoading.set(true);
    this.albumService
      .getAlbumImages({ groupId: this.groupId(), albumId: album.id! })
      .pipe(take(1))
      .subscribe({
        next: (images) => {
          this.albumImages.set(images);
          this.albumImagesLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.albumImagesLoading.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  async pickAlbumImage(image: AlbumImage): Promise<void> {
    const url = image.variants?.laptop || image.variants?.original || image.variants?.mobile;
    if (!url) return;
    this.showPhotoPicker.set(false);
    this.isBusy.set(true);
    try {
      await this.canvasService.addLayer({
        id: crypto.randomUUID(),
        type: 'photo',
        x: this.artboardSize.width / 2,
        y: this.artboardSize.height / 2,
        width: 0,
        height: 0,
        rotation: 0,
        zIndex: 0,
        props: { sourceUrl: url },
      });
    } catch (err) {
      console.error('ImageEditorComponent :: Failed to add album photo:', err);
      this.editorError.set("Couldn't load that photo. Please try again, or upload it from your device instead.");
    } finally {
      this.isBusy.set(false);
      this.cdr.markForCheck();
    }
  }

  async onPhotoFileSelected(event: Event): Promise<void> {
    const file = (event.target as HTMLInputElement).files?.[0];
    (event.target as HTMLInputElement).value = '';
    if (!file) return;
    const dataUrl = await this.readFileAsDataUrl(file);
    this.isBusy.set(true);
    try {
      await this.canvasService.addLayer({
        id: crypto.randomUUID(),
        type: 'photo',
        x: this.artboardSize.width / 2,
        y: this.artboardSize.height / 2,
        width: 0,
        height: 0,
        rotation: 0,
        zIndex: 0,
        props: { sourceUrl: dataUrl },
      });
    } catch (err) {
      console.error('ImageEditorComponent :: Failed to add photo:', err);
      this.editorError.set("Couldn't add that photo. Please try again.");
    } finally {
      this.isBusy.set(false);
      this.cdr.markForCheck();
    }
  }

  /** Left-rail "Replace image" for whichever image layer is currently selected. */
  triggerReplaceSelectedImage(): void {
    this.replaceFileInput?.nativeElement.click();
  }

  async onReplaceFileSelected(event: Event): Promise<void> {
    const file = (event.target as HTMLInputElement).files?.[0];
    (event.target as HTMLInputElement).value = '';
    const layer = this.activeLayer();
    if (!file || !layer) return;
    const dataUrl = await this.readFileAsDataUrl(file);
    this.isBusy.set(true);
    try {
      await this.canvasService.replaceImageLayerSource(layer.id, dataUrl);
    } catch (err) {
      console.error('ImageEditorComponent :: Failed to replace photo:', err);
      this.editorError.set("Couldn't replace that photo. Please try again.");
    } finally {
      this.isBusy.set(false);
      this.cdr.markForCheck();
    }
  }

  /** Defaults the QR prompt to this group's own invite/join link (the same link shared elsewhere in the app, e.g. "Invite members") — the common case for a notice/event banner QR is "scan to join our group", and the field stays fully editable for any other URL. */
  openQrPrompt(): void {
    this.qrUrlInput = `https://${environment.domain}/links/groupinvite?groupId=${this.groupId()}`;
    this.showQrPrompt.set(true);
  }

  closeQrPrompt(): void {
    this.showQrPrompt.set(false);
  }

  async confirmAddQr(): Promise<void> {
    const url = this.qrUrlInput.trim();
    if (!url) return;
    this.showQrPrompt.set(false);
    this.isBusy.set(true);
    try {
      await this.canvasService.addLayer({
        id: crypto.randomUUID(),
        type: 'qr',
        x: this.artboardSize.width / 2,
        y: this.artboardSize.height / 2,
        width: 0,
        height: 0,
        rotation: 0,
        zIndex: 0,
        props: { targetUrl: url },
      });
    } catch (err) {
      console.error('ImageEditorComponent :: Failed to add QR code:', err);
      this.editorError.set("Couldn't generate that QR code. Please try again.");
    } finally {
      this.isBusy.set(false);
      this.cdr.markForCheck();
    }
  }

  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // ─────────────────────────────────────────────
  // RIGHT PANEL — ATTRIBUTES OF THE SELECTED LAYER
  // ─────────────────────────────────────────────

  round(n: number): number {
    return Math.round(n);
  }

  layerIcon(type: EditorLayerType, isGroup = false): string {
    if (isGroup) return 'fa fa-object-group';
    switch (type) {
      case 'text':
        return 'fa fa-font';
      case 'decoration':
        return 'fa fa-shapes';
      case 'qr':
        return 'fa fa-qrcode';
      case 'photo':
      case 'logo':
      case 'background-image':
        return 'fa fa-image';
      default:
        return 'fa fa-layer-group';
    }
  }

  layerLabel(type: EditorLayerType, isGroup = false): string {
    if (isGroup) return 'Group';
    switch (type) {
      case 'background-image':
      case 'logo':
        return 'Photo';
      case 'decoration':
        return 'Shape';
      default:
        return type;
    }
  }

  /** True for a layer created via multi-select → Group (see groupSelectedLayers()). */
  isGroupLayer(layer: EditorLayer): boolean {
    return layer.type === 'decoration' && layer.props?.['shapeType'] === 'group';
  }

  updateActive(props: Record<string, any>): void {
    const layer = this.activeLayer();
    if (!layer) return;
    this.canvasService.updateLayer(layer.id, props);
  }

  // ── Transparency popovers — a slider (0-100) replaces the old on/off eye
  // toggle for every color row (fill, text background, shadow, text
  // outline). showOpacityPopupFor tracks which single one is open at a time,
  // keyed by a short string ('fill' | 'background' | 'shadow' | 'textStroke')
  // since several of these repeat across text/shape/photo. ──

  showOpacityPopupFor = signal<string | null>(null);

  toggleOpacityPopup(key: string): void {
    this.showOpacityPopupFor.update((v) => (v === key ? null : key));
  }

  /** Reads back the effective 0-100 opacity for a color row, matching the exact same fallback the canvas service uses when actually rendering it (see ImageEditorCanvasService), so the slider always reflects what's really on the canvas — including for scenes saved before this slider existed. */
  currentOpacity(layer: EditorLayer, key: 'fill' | 'background' | 'shadow' | 'textStroke'): number {
    const props = layer.props || {};
    switch (key) {
      case 'fill':
        return props['fillOpacity'] ?? (props['fillTransparent'] ? 0 : 100);
      case 'background':
        return props['backgroundOpacity'] ?? (props['backgroundColor'] ? 100 : 0);
      case 'shadow':
        return props['shadowOpacity'] ?? (props['shadowEnabled'] ? 100 : 0);
      case 'textStroke':
        return props['textStrokeOpacity'] ?? (props['textStrokeEnabled'] ? 100 : 0);
    }
  }

  /** Sets a color row's transparency (0-100) — bumping in a sensible default color the first time a previously-empty one is made visible. */
  setOpacity(key: 'fill' | 'background' | 'shadow' | 'textStroke', value: number): void {
    const layer = this.activeLayer();
    switch (key) {
      case 'fill':
        this.updateActive({ fillOpacity: value, fill: layer?.props?.['fill'] || '#2781ff' });
        break;
      case 'background':
        this.updateActive({
          backgroundOpacity: value,
          backgroundColor: layer?.props?.['backgroundColor'] || '#ffe27a',
        });
        break;
      case 'shadow':
        this.updateActive({
          shadowOpacity: value,
          shadowColor: layer?.props?.['shadowColor'] || '#000000',
          shadowBlur: layer?.props?.['shadowBlur'] ?? 14,
          shadowOffsetX: layer?.props?.['shadowOffsetX'] ?? 4,
          shadowOffsetY: layer?.props?.['shadowOffsetY'] ?? 4,
        });
        break;
      case 'textStroke':
        this.updateActive({
          textStrokeOpacity: value,
          textStrokeColor: layer?.props?.['textStrokeColor'] || '#000000',
          textStrokeWidth: layer?.props?.['textStrokeWidth'] ?? 2,
        });
        break;
    }
  }

  // ── Eyedropper: pick a color straight off the artboard (or anywhere on
  // screen) via the browser's EyeDropper API. Chromium-only as of writing —
  // eyeDropperSupported feature-detects so the button just doesn't render
  // in browsers that lack it, rather than showing a dead button. ──

  get eyeDropperSupported(): boolean {
    return typeof window !== 'undefined' && 'EyeDropper' in window;
  }

  private async eyeDropperPick(): Promise<string | null> {
    const EyeDropperCtor = (window as any).EyeDropper;
    if (!EyeDropperCtor) return null;
    try {
      const result = await new EyeDropperCtor().open();
      return result?.sRGBHex ?? null;
    } catch {
      return null; // user cancelled the pick (Escape / clicked away) — not an error
    }
  }

  async pickTextColor(): Promise<void> {
    const hex = await this.eyeDropperPick();
    if (hex) this.updateActive({ color: hex });
  }

  /** Picking a color also makes it visible (bumps opacity up from 0) if it was fully transparent — a picked-but-invisible color would be confusing. */
  async pickTextBackgroundColor(): Promise<void> {
    const hex = await this.eyeDropperPick();
    if (!hex) return;
    const layer = this.activeLayer();
    this.updateActive({ backgroundColor: hex, backgroundOpacity: this.currentOpacity(layer!, 'background') || 100 });
  }

  async pickFillColor(): Promise<void> {
    const hex = await this.eyeDropperPick();
    if (!hex) return;
    const layer = this.activeLayer();
    this.updateActive({ fill: hex, fillOpacity: this.currentOpacity(layer!, 'fill') || 100 });
  }

  async pickStrokeColor(): Promise<void> {
    const hex = await this.eyeDropperPick();
    if (hex) this.updateActive({ stroke: hex });
  }

  async pickTextStrokeColor(): Promise<void> {
    const hex = await this.eyeDropperPick();
    if (!hex) return;
    const layer = this.activeLayer();
    this.updateActive({
      textStrokeColor: hex,
      textStrokeOpacity: this.currentOpacity(layer!, 'textStroke') || 100,
      textStrokeWidth: layer?.props?.['textStrokeWidth'] ?? 2,
    });
  }

  /** Shared by text/shape/photo shadow rows — picking a color also makes the shadow visible, since a picked-but-invisible color would be confusing. */
  async pickShadowColor(): Promise<void> {
    const hex = await this.eyeDropperPick();
    if (!hex) return;
    const layer = this.activeLayer();
    this.updateActive({
      shadowColor: hex,
      shadowOpacity: this.currentOpacity(layer!, 'shadow') || 100,
      shadowBlur: layer?.props?.['shadowBlur'] ?? 14,
      shadowOffsetX: layer?.props?.['shadowOffsetX'] ?? 4,
      shadowOffsetY: layer?.props?.['shadowOffsetY'] ?? 4,
    });
  }

  /** Brush texture presets offered wherever fillMode/strokeMode is 'brush' — shape fill, shape border, and text color. */
  readonly brushStyles = BRUSH_STYLES;

  setFillMode(mode: 'solid' | 'gradient' | 'brush'): void {
    this.updateActive({ fillMode: mode });
  }

  setStrokeMode(mode: 'solid' | 'gradient' | 'brush'): void {
    this.updateActive({ strokeMode: mode });
  }

  /** The chosen fill-brush texture (shape fill or text color), defaulting the dropdown to the first preset for a layer that hasn't picked one yet. */
  currentBrushStyle(layer: EditorLayer): string {
    return layer.props?.['brushStyle'] || DEFAULT_BRUSH_STYLE;
  }

  setBrushStyle(style: string): void {
    this.updateActive({ brushStyle: style });
  }

  /** The chosen border-brush texture, independent from the fill's brush style so a shape can mix, e.g., a solid fill with a brush border. */
  currentStrokeBrushStyle(layer: EditorLayer): string {
    return layer.props?.['strokeBrushStyle'] || DEFAULT_BRUSH_STYLE;
  }

  setStrokeBrushStyle(style: string): void {
    this.updateActive({ strokeBrushStyle: style });
  }

  /** 'none' hides the border entirely (the "invisible border" option); 'dashed'/'dotted' set a strokeDashArray in the canvas service. */
  setStrokeStyle(style: 'none' | 'solid' | 'dashed' | 'dotted'): void {
    this.updateActive({ strokeStyle: style });
  }

  /** Resolves the effective border style, falling back to 'solid'/'none' for scenes saved before strokeStyle existed. */
  strokeStyleOf(layer: EditorLayer): 'none' | 'solid' | 'dashed' | 'dotted' {
    return (layer.props?.['strokeStyle'] as 'none' | 'solid' | 'dashed' | 'dotted' | undefined) ||
      (layer.props?.['strokeWidth'] ? 'solid' : 'none');
  }

  /** 'none' turns blur off; 'radial' is a real, uniform Gaussian blur, 'linear' fakes a directional motion blur (see ImageEditorCanvasService.applyBlur for why shapes can't just use Fabric's image filters). */
  setShapeBlurType(type: 'none' | 'linear' | 'radial'): void {
    const layer = this.activeLayer();
    this.updateActive({ blurType: type, blurAmount: layer?.props?.['blurAmount'] ?? 8 });
  }

  // ── Multi-stop gradients — shared by Fill and Border ──

  readonly defaultGradientStops: { offset: number; color: string }[] = [
    { offset: 0, color: '#2781ff' },
    { offset: 100, color: '#ff2ee1' },
  ];

  private patchGradientStops(
    propKey: 'fillGradientStops' | 'strokeGradientStops',
    mutate: (stops: { offset: number; color: string }[]) => { offset: number; color: string }[],
  ): void {
    const layer = this.activeLayer();
    if (!layer) return;
    const current = (layer.props?.[propKey] as { offset: number; color: string }[] | undefined) || this.defaultGradientStops;
    this.updateActive({ [propKey]: mutate(current.map((s) => ({ ...s }))) });
  }

  updateGradientStop(
    propKey: 'fillGradientStops' | 'strokeGradientStops',
    index: number,
    patch: Partial<{ offset: number; color: string }>,
  ): void {
    this.patchGradientStops(propKey, (stops) => {
      stops[index] = { ...stops[index], ...patch };
      return stops;
    });
  }

  addGradientStop(propKey: 'fillGradientStops' | 'strokeGradientStops'): void {
    this.patchGradientStops(propKey, (stops) => {
      const last = stops[stops.length - 1];
      stops.push({ offset: Math.max(0, Math.min(100, last?.offset ?? 100)), color: '#ffffff' });
      return stops;
    });
  }

  removeGradientStop(propKey: 'fillGradientStops' | 'strokeGradientStops', index: number): void {
    this.patchGradientStops(propKey, (stops) =>
      stops.length > 2 ? stops.filter((_, i) => i !== index) : stops,
    );
  }

  async updateQrTargetUrl(url: string): Promise<void> {
    const layer = this.activeLayer();
    if (!layer) return;
    this.isBusy.set(true);
    try {
      await this.canvasService.regenerateQr(layer.id, url);
    } finally {
      this.isBusy.set(false);
      this.cdr.markForCheck();
    }
  }

  deleteActive(): void {
    const layer = this.activeLayer();
    if (!layer) return;
    this.canvasService.removeLayer(layer.id);
  }

  bringForward(): void {
    const layer = this.activeLayer();
    if (layer) this.canvasService.bringForward(layer.id);
  }

  sendBackward(): void {
    const layer = this.activeLayer();
    if (layer) this.canvasService.sendBackward(layer.id);
  }

  // ─────────────────────────────────────────────
  // LAYERS PANEL — full z-order list, always visible below the attributes area
  // ─────────────────────────────────────────────

  /** Selects a row's layer on the canvas — the row itself also drives the attributes panel above via activeLayer(). */
  selectLayerRow(layerId: string): void {
    this.canvasService.selectLayer(layerId);
  }

  /** Moves a layer one step forward/backward in z-order, independent of whether it's currently selected. */
  reorderLayer(layerId: string, direction: 'up' | 'down', event?: Event): void {
    event?.stopPropagation();
    if (direction === 'up') {
      this.canvasService.bringForward(layerId);
    } else {
      this.canvasService.sendBackward(layerId);
    }
  }

  /** Short row label — a user-set custom name takes priority, then the text content for text layers (so multiple are distinguishable), a "Group xxxx" placeholder (short id fragment) for an unnamed group, otherwise the generic type label. */
  layerRowLabel(layer: EditorLayer): string {
    const customName = (layer.props?.['customName'] as string | undefined)?.trim();
    if (customName) {
      return customName.length > 24 ? `${customName.slice(0, 24)}…` : customName;
    }
    if (layer.type === 'text') {
      const text = ((layer.props?.['text'] as string) || '').replace(/\s+/g, ' ').trim();
      if (!text) return 'Text';
      return text.length > 24 ? `${text.slice(0, 24)}…` : text;
    }
    if (this.isGroupLayer(layer)) {
      return `Group ${layer.id.replace(/-/g, '').slice(0, 4).toUpperCase()}`;
    }
    return this.layerLabel(layer.type);
  }

  /** Double-click a layer row's name to rename it — stored as props.customName, a purely cosmetic label that never triggers a canvas rebuild (see ImageEditorCanvasService.updateLayer). */
  startRenameLayer(layer: EditorLayer, event?: Event): void {
    event?.stopPropagation();
    this.editingLayerName = this.layerRowLabel(layer);
    this.editingLayerId.set(layer.id);
  }

  commitRenameLayer(layerId: string): void {
    if (this.editingLayerId() !== layerId) return; // already committed/cancelled (e.g. Enter then blur firing both)
    const name = this.editingLayerName.trim();
    this.editingLayerId.set(null);
    if (name) this.canvasService.updateLayer(layerId, { customName: name });
  }

  cancelRenameLayer(): void {
    this.editingLayerId.set(null);
  }

  // ─────────────────────────────────────────────
  // APPLY / CANCEL
  // ─────────────────────────────────────────────

  async onSave(): Promise<void> {
    if (this.isSaving()) return;
    this.isSaving.set(true);
    this.cdr.markForCheck();
    try {
      this.canvasService.deselect();
      const blob = await this.canvasService.exportToBlob('png');
      const scene = this.canvasService.getScene();

      const endpoint = this.preSignedUrlEndpoint();
      if (!endpoint) {
        throw new Error('Image upload isn’t configured for this editor instance.');
      }

      const uid = this.auth.currentUser?.uid || 'unknown';
      const fileName = `${this.contextLabel()}_${uid}_${Date.now()}.png`;
      const headers = await this.utilsService.setAxiosHeaders(true, undefined, this.s3ApiKey());

      // Same request shape as app-s3-image-upload's getSignedUrl(): the
      // backend Lambda behind this endpoint returns a pre-signed PUT url
      // scoped to this group/notice — never Firebase Storage.
      const signedUrlResponse = await axios.get<{ url: string }>(endpoint, {
        params: {
          uid,
          name: fileName,
          fileExtension: 'png',
          ...this.uploadMetadata(),
          aspectRatio: '1',
        },
        headers,
      });

      if (signedUrlResponse.status !== 200) {
        throw new Error('Failed to get an upload URL.');
      }

      const uploadUrl = signedUrlResponse.data.url;
      await axios.put(uploadUrl, blob, { headers: { 'Content-Type': 'image/png' } });

      const cfDomain = this.cloudFrontDomain();
      const imageUrl = cfDomain ? UtilsService.replaceDomain(uploadUrl, cfDomain) : uploadUrl;

      this.lastSavedScene = JSON.parse(JSON.stringify(scene));
      this.isDirty.set(false);
      this.dirty.emit(false);
      this.saved.emit({ imageUrl, scene });
    } catch (err) {
      console.error('ImageEditorComponent :: Failed to save image:', err);
      this.editorError.set(
        (err instanceof Error && err.message) || "Couldn't save the image. Please try again.",
      );
    } finally {
      this.isSaving.set(false);
      this.cdr.markForCheck();
    }
  }

  /** Reverts the canvas to the last saved state, discarding any unsaved edits — shown only while isDirty(). */
  async onCancelEdits(): Promise<void> {
    if (this.isSaving() || this.isBusy()) return;
    this.isBusy.set(true);
    this.cdr.markForCheck();
    try {
      const scene = this.lastSavedScene ?? {
        version: 1 as const,
        aspectRatio: this.aspectRatio(),
        layers: [],
      };
      await this.canvasService.loadScene(scene);
      this.isDirty.set(false);
      this.dirty.emit(false);
    } catch (err) {
      console.error('ImageEditorComponent :: Failed to revert edits:', err);
      this.editorError.set("Couldn't revert to the saved version. Please try again.");
    } finally {
      this.isBusy.set(false);
      this.cdr.markForCheck();
    }
  }

  /** Downloads the current artboard as a PNG straight from the browser — no upload, unlike Save. */
  async exportImage(): Promise<void> {
    if (this.isBusy() || this.isSaving()) return;
    this.isBusy.set(true);
    this.cdr.markForCheck();
    try {
      this.canvasService.deselect();
      const blob = await this.canvasService.exportToBlob('png');
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${this.contextLabel()}-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('ImageEditorComponent :: Failed to export image:', err);
      this.editorError.set(
        (err instanceof Error && err.message) || "Couldn't export the image. Please try again.",
      );
    } finally {
      this.isBusy.set(false);
      this.cdr.markForCheck();
    }
  }

  // ─────────────────────────────────────────────
  // IMPORT / EXPORT LAYERED FILES — save the full editable scene (not a
  // flattened image) to a local .json file so it can be reopened later for
  // another notice/event banner, and reload one back in.
  // ─────────────────────────────────────────────

  /** Downloads the current scene (every layer + its props) as a local JSON file — reopenable later via the File rail button's Import option, on this or any other notice/event. */
  exportTemplate(): void {
    this.showFileMenu.set(false);
    try {
      const scene = this.canvasService.getScene();
      const payload = { kind: 'atc-image-editor-layers', savedAt: new Date().toISOString(), ...scene };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${this.contextLabel()}-layers-${Date.now()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('ImageEditorComponent :: Failed to save layered file:', err);
      this.editorError.set("Couldn't save the layered file. Please try again.");
    }
  }

  triggerImportTemplate(): void {
    this.showFileMenu.set(false);
    this.importFileInput?.nativeElement.click();
  }

  async onImportFileSelected(event: Event): Promise<void> {
    const file = (event.target as HTMLInputElement).files?.[0];
    (event.target as HTMLInputElement).value = '';
    if (!file) return;

    if (
      this.layers().length > 0 &&
      !window.confirm('Importing will replace everything currently on the artboard. Continue?')
    ) {
      return;
    }

    this.isBusy.set(true);
    this.cdr.markForCheck();
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!parsed || !Array.isArray(parsed.layers)) {
        throw new Error("That file doesn't look like a layered image saved from this editor.");
      }
      const scene: EditorScene = {
        version: 1,
        aspectRatio: parsed.aspectRatio || this.aspectRatio(),
        layers: parsed.layers,
      };
      await this.canvasService.loadScene(scene);
      this.activeLayer.set(null);
      this.isDirty.set(true);
      this.dirty.emit(true);
      this.zoomToFit();
    } catch (err) {
      console.error('ImageEditorComponent :: Failed to import layered file:', err);
      this.editorError.set(
        (err instanceof Error && err.message) ||
          "Couldn't open that file. Please make sure it's a layered image file saved from this editor.",
      );
    } finally {
      this.isBusy.set(false);
      this.cdr.markForCheck();
    }
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  // ─────────────────────────────────────────────
  // AI COMMAND BOUNDARY (see IMAGE_EDITOR_REQUIREMENTS.md §9)
  // ─────────────────────────────────────────────

  /**
   * Entry point for a host page's AI intent router to act on the canvas —
   * e.g. after classifying "make the title bigger" as an update-layer
   * command targeting a real layerId.
   */
  applyAiCommand(command: EditorAiCommand): void {
    switch (command.type) {
      case 'set-background': {
        const existingBg = this.canvasService
          .getScene()
          .layers.find((l) => l.type === 'background-image');
        if (existingBg) {
          this.canvasService.replaceImageLayerSource(existingBg.id, command.sourceUrl);
        } else {
          this.canvasService.addLayer({
            id: 'background',
            type: 'background-image',
            x: 0,
            y: 0,
            width: 0,
            height: 0,
            rotation: 0,
            zIndex: 0,
            props: { sourceUrl: command.sourceUrl },
          });
        }
        break;
      }
      case 'update-layer':
        this.canvasService.updateLayer(command.layerId, command.props);
        break;
      case 'add-layer': {
        const props = { ...command.props };
        // Same default as the manual "Add QR" toolbar button: if the AI (or
        // whatever's calling this) adds a QR layer without saying what it
        // should link to, point it at this group's own invite/join link
        // rather than adding a broken, empty QR code.
        if (command.layerType === 'qr' && !props['targetUrl']) {
          props['targetUrl'] = `https://${environment.domain}/links/groupinvite?groupId=${this.groupId()}`;
        }
        this.canvasService.addLayer({
          id: crypto.randomUUID(),
          type: command.layerType,
          x: this.artboardSize.width / 2,
          y: this.artboardSize.height / 2,
          width: 0,
          height: 0,
          rotation: 0,
          zIndex: 0,
          props,
        });
        break;
      }
      case 'remove-layer':
        this.canvasService.removeLayer(command.layerId);
        break;
    }
  }

  /**
   * Short summaries of the current layers, for a host page to include in AI
   * chat context so the model can ground "the title" / "the background" in
   * a real layerId rather than guessing.
   */
  getLayerSummaries(): LayerSummary[] {
    try {
      return this.canvasService.getScene().layers.map((layer) => ({
        id: layer.id,
        type: layer.type,
        label: (layer.props?.['text'] as string) || layer.type,
      }));
    } catch {
      return [];
    }
  }
}
